import { Badge } from '@/components/ui/badge';
import { StatusBadge } from '@/components/ui/status-badge';
import {
  NON_PROVING_REASON_LABELS,
  type NonProvingReason
} from '@/lib/constants';

export type ContactRole = 'lpr' | 'beneficiary' | 'client';

/**
 * Canonical role badge (LF-4899 Variant A): an outlined pill — LPR,
 * Beneficiary, or Client (the non-estate matter role, LF-5788) — fixed at
 * text-xs. This is the single source of truth for role-badge styling; render it
 * everywhere these roles appear so every surface presents them identically.
 *
 * A role is a *type label*, not a semantic state, so it stays a structural
 * `Badge` rather than moving to `StatusBadge` (LF-6155 / BADGES-7): the tone
 * ramp means "good / waiting / broken", and spending `ai` on an LPR or
 * `success` on a Client would say something untrue about all three. Its former
 * per-role purple / blue / emerald `/60`-alpha paste is gone with the same
 * migration — those were near-duplicates of ramps the epic consolidates (the
 * beneficiary pill's was byte-identical to one LF-6149 had already removed).
 * The label is what names the role; where a badge needs to *rank* rather than
 * *name*, that is what `StatusBadge` is for.
 */
const ROLE_LABEL: Record<ContactRole, string> = {
  lpr: 'LPR',
  beneficiary: 'Beneficiary',
  client: 'Client'
};

interface ContactRoleBadgeProps {
  role: ContactRole;
  className?: string;
}

export function ContactRoleBadge({ role, className }: ContactRoleBadgeProps) {
  return (
    <Badge variant="outline" className={className}>
      {ROLE_LABEL[role]}
    </Badge>
  );
}

/**
 * Non-proving status modifier for a non-proving LPR, rendered alongside the
 * `lpr` ContactRoleBadge (never standalone), not a new ContactRole: a
 * non-proving LPR is still an LPR. The label reflects the *specific* reason
 * the LPR is non-proving (Leave reserved, Renunciation, Predeceased,
 * Incapacity, Passed over); a null/absent reason falls back to "Leave
 * reserved", the historical implicit reason for LPRs marked non-proving
 * before the reason was captured. See LF-5342, LF-5372.
 *
 * Unlike the role badge above this *is* a semantic state — it qualifies how
 * far the LPR can act — so it carries the `caution` tone (LF-6155). Its old
 * amber `/60`-alpha paste was that ramp restated at an alpha.
 */
export function NonProvingBadge({
  reason,
  className
}: {
  reason?: NonProvingReason | null;
  className?: string;
}) {
  const label = reason
    ? NON_PROVING_REASON_LABELS[reason]
    : NON_PROVING_REASON_LABELS.leave_reserved;
  return (
    <StatusBadge tone="caution" className={className}>
      {label}
    </StatusBadge>
  );
}
